import React from "react";
import { motion } from "framer-motion";
import Container from "components/Container";
import CreateAccountModal from "components/CreateAccountModal";
import DownloadModal from "components/DownloadModal";
import MobileDownloadModal from "components/MobileDownloadModal";
import { useOneChat } from "hooks/useOneChat";
import { useSignupForm } from "hooks/useSignupForm";
import { useMyStore } from "store";
import TurnstileWidget, {
  TurnstileWidgetHandle,
} from "components/TurnstileWidget";

import styles from "./styles.module.scss";

import LogoWinAZ from "assets/img-winaz/logo.webp";
import TypoWinAZ from "assets/img-winaz/txt3.webp";
import BackgroundDesktop from "assets/img-winaz/background/bgngang3.webp";
import BackgroundMobile from "assets/img-winaz/background/bgdoc3.webp";
import ButtonPlayWeb from "assets/img-winaz/button/btnchoiwweb2.webp";
import ButtonRegister from "assets/img-winaz/button/btndawng ki.webp";
import ButtonDownload from "assets/img-winaz/button/btntaigame2.webp";
import ButtonScrollDown from "assets/img-winaz/button/btn-down.webp";
import ButtonScrollUp from "assets/img-winaz/button/btn-up.webp";
import RightBanner1 from "assets/img-winaz/banner/banner_3-1.webp";
import RightBanner2 from "assets/img-winaz/banner/banner_3-2.webp";
import RightBanner3 from "assets/img-winaz/banner/banner_3-3.webp";
import RightBanner4 from "assets/img-winaz/banner/banner_3-4.webp";
import IconSound from "assets/img-winaz/icon/icon-sound.svg";
import IconUnsound from "assets/img-winaz/icon/icon-unsound.svg";
import LabelX3 from "assets/img-winaz/button/label29k.webp";
import BgTaiGame from "assets/img-winaz/background/bgtaigame.webp";
import IconBlockchain from "assets/img-winaz/icon-baomat/icon-blockchain.webp";
import IconMd5 from "assets/img-winaz/icon-baomat/icon-md5.webp";
import IconGamingLabs from "assets/img-winaz/icon-baomat/icon-gaming-labs.webp";
import ProviderLogo1 from "assets/img-winaz/icon-provider/logo1.webp";
import ProviderLogo2 from "assets/img-winaz/icon-provider/logo2.webp";
import ProviderLogo3 from "assets/img-winaz/icon-provider/logo3.webp";
import ProviderLogo4 from "assets/img-winaz/icon-provider/logo4.webp";
import ProviderLogo5 from "assets/img-winaz/icon-provider/logo5.webp";
import LogoGalaxy from "assets/img-winaz/logogalaxy.webp";
import BgNoti from "assets/img-winaz/noti-congrat/bgnoti.webp";
import InputBackground from "assets/img-winaz/signup-form/input.webp";
import IconUser from "assets/img-winaz/signup-form/iconusser.webp";
import IconPassword from "assets/img-winaz/signup-form/iconpassword.webp";
import FormBackground from "assets/img-winaz/signup-form/bgpopup3.webp";
import SocialFacebook from "assets/img-winaz/social-icon/001-facebook.webp";
import SocialTelegram from "assets/img-winaz/social-icon/002-telegram.webp";
import IntroImage1 from "assets/img-winaz/section-intro/anh1.webp";
import IntroImage2 from "assets/img-winaz/section-intro/anh2.webp";
import IntroImage3 from "assets/img-winaz/section-intro/anh3.webp";
import IntroImage4 from "assets/img-winaz/section-intro/anh4.webp";
import IntroImage5 from "assets/img-winaz/section-intro/anh5.webp";
import Certificate1 from "assets/img-winaz/ certificate/Group 2046.webp";
import Certificate2 from "assets/img-winaz/ certificate/Group 2047.webp";
import Certificate3 from "assets/img-winaz/ certificate/Group 2048.webp";
import Certificate4 from "assets/img-winaz/ certificate/Group 2049.webp";
import Certificate5 from "assets/img-winaz/ certificate/Group 2050.webp";
import Certificate6 from "assets/img-winaz/ certificate/Group 2051.webp";
import { getPlayGameDomain } from "utils";
import { flushEngagementThen } from "utils/dataLayer";
import { NOTI_ITEMS, type NotiItem } from "./constants";
import { SOCIAL_LINKS } from "const";

type Platform = "android" | "ios" | "unknown";

const detectPlatform = (userAgent: string): Platform => {
  if (/android/i.test(userAgent)) {
    return "android";
  }
  if (/iPhone|iPad|iPod|iOS/i.test(userAgent)) {
    return "ios";
  }
  return "unknown";
};

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

type SliderItem = {
  id: number;
  src: string;
  alt: string;
};

type SocialLink = {
  id: string;
  icon: string;
  alt: string;
  url: string;
  target?: "_self" | "_blank";
};

const SOCIAL_ITEMS: SocialLink[] = [
  {
    id: "facebook",
    icon: SocialFacebook,
    alt: "Facebook",
    url: SOCIAL_LINKS.facebook,
    target: "_blank",
  },
  {
    id: "telegram",
    icon: SocialTelegram,
    alt: "Telegram",
    url: SOCIAL_LINKS.telegram,
    target: "_blank",
  },
];

const CERTIFICATE_IMAGES = [
  Certificate1,
  Certificate2,
  Certificate3,
  Certificate4,
  Certificate5,
  Certificate6,
];
const SLIDE_TRANSITION_DURATION = 700;
const SLIDE_SWIPE_THRESHOLD = 60;
type ActiveNoti = {
  uid: number;
  item: NotiItem;
  exiting: boolean;
};

const NOTI_DISPLAY_DURATION = 6000;
const NOTI_EXIT_ANIMATION_DURATION = 600;
const NOTI_BURST_PROBABILITY = 0.22;
const NOTI_BURST_DELAY_MIN = 800;
const NOTI_BURST_DELAY_MAX = 1500;
const NOTI_NORMAL_DELAY_MIN = 8000;
const NOTI_NORMAL_DELAY_MAX = 16000;
const NOTI_MAX_VISIBLE = 2;
const NOTI_INITIAL_DELAY = 2500;

const SIDE_BANNER_ROTATION_INTERVAL = 5000;
const SIDE_BANNER_ANIMATION_DURATION = 700;

type SideBannerState = {
  previous: number | null;
  isAnimating: boolean;
};

const RIGHT_DESKTOP_BANNERS: SliderItem[] = [
  { id: 0, src: RightBanner1, alt: "WinAZ banner 1" },
  { id: 1, src: RightBanner2, alt: "WinAZ banner 2" },
  { id: 2, src: RightBanner3, alt: "WinAZ banner 3" },
  { id: 3, src: RightBanner4, alt: "WinAZ banner 4" },
];

const INTERACTIVE_IMAGE_PROPS = {
  initial: { filter: "brightness(1)", scale: 1 },
  whileHover: { filter: "brightness(1.08)", scale: 1.03 },
  whileTap: { scale: 0.96 },
  transition: {
    type: "spring" as const,
    stiffness: 450,
    damping: 32,
    mass: 0.28,
  },
};

const Home: React.FunctionComponent = () => {
  const resourceDomain = useMyStore((s) => s.bootstrap?.resourceDomain);
  const [isMuted, setIsMuted] = React.useState(true);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [activeNotis, setActiveNotis] = React.useState<ActiveNoti[]>([]);
  const notiUidRef = React.useRef(0);
  const notiSchedulerRef = React.useRef<number | null>(null);
  const notiShuffledRef = React.useRef<number[]>([]);
  const notiCursorRef = React.useRef(0);
  const [rightBannerIndex, setRightBannerIndex] = React.useState(0);
  const [rightBannerState, setRightBannerState] =
    React.useState<SideBannerState>({ previous: null, isAnimating: false });
  const rightBannerAnimationTimeoutRef = React.useRef<number | null>(null);
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] =
    React.useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);
  const [isMobileDownloadModalOpen, setIsMobileDownloadModalOpen] =
    React.useState(false);
  const [mobilePlatform, setMobilePlatform] = React.useState<"android" | "ios">(
    "android",
  );
  const [activeSlide, setActiveSlide] = React.useState(0);
  const sliderRef = React.useRef<HTMLDivElement | null>(null);
  const isTransitioningRef = React.useRef(false);
  const transitionTimeoutRef = React.useRef<number>();
  const touchStartYRef = React.useRef<number | null>(null);
  const touchScrollTargetRef = React.useRef<HTMLElement | null>(null);
  const trackedSlidesRef = React.useRef<Set<number>>(new Set());
  const turnstileWidgetRef = React.useRef<TurnstileWidgetHandle | null>(null);
  const hasTrackedInlineFormStartRef = React.useRef(false);
  const usernameInputRef = React.useRef<HTMLInputElement | null>(null);
  const handleResetTurnstile = React.useCallback(() => {
    turnstileWidgetRef.current?.reset();
  }, []);
  const pushDataLayerEvent = React.useCallback(
    (eventName: string, payload: Record<string, any> = {}) => {
      if (typeof window === "undefined") {
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        ...payload,
      });
    },
    [],
  );
  const trackInlineFormStart = React.useCallback(() => {
    if (hasTrackedInlineFormStartRef.current) {
      return;
    }

    hasTrackedInlineFormStartRef.current = true;
    pushDataLayerEvent("lp_form_start", { form_id: "register_inline" });
  }, [pushDataLayerEvent]);
  const trackRegisterCta = React.useCallback(
    (location: string) => {
      pushDataLayerEvent("lp_click_cta_register", { location });
    },
    [pushDataLayerEvent],
  );
  const {
    username,
    password,
    apiError,
    usernameError,
    passwordError,
    isSubmitting,
    turnstileRequired,
    handleSubmit: handleInlineSubmit,
    handleUsernameChange,
    handlePasswordChange,
    handleTurnstileTokenChange,
  } = useSignupForm({
    requireConfirmPassword: false,
    formId: "register_inline",
    onTurnstileReset: handleResetTurnstile,
  });

  useOneChat();

  React.useEffect(() => {
    const rand = (min: number, max: number) => min + Math.random() * (max - min);

    const getNextDelay = () =>
      Math.random() < NOTI_BURST_PROBABILITY
        ? rand(NOTI_BURST_DELAY_MIN, NOTI_BURST_DELAY_MAX)
        : rand(NOTI_NORMAL_DELAY_MIN, NOTI_NORMAL_DELAY_MAX);

    const showNext = () => {
      if (notiCursorRef.current >= notiShuffledRef.current.length) {
        notiShuffledRef.current = [...Array(NOTI_ITEMS.length).keys()].sort(
          () => Math.random() - 0.5,
        );
        notiCursorRef.current = 0;
      }

      const itemIndex = notiShuffledRef.current[notiCursorRef.current++];
      const item = NOTI_ITEMS[itemIndex];
      const uid = ++notiUidRef.current;

      setActiveNotis((prev) => {
        const limited =
          prev.length >= NOTI_MAX_VISIBLE ? prev.slice(prev.length - (NOTI_MAX_VISIBLE - 1)) : prev;
        return [...limited, { uid, item, exiting: false }];
      });

      const displayDuration = NOTI_DISPLAY_DURATION;

      window.setTimeout(() => {
        setActiveNotis((prev) =>
          prev.map((n) => (n.uid === uid ? { ...n, exiting: true } : n)),
        );
        window.setTimeout(() => {
          setActiveNotis((prev) => prev.filter((n) => n.uid !== uid));
        }, NOTI_EXIT_ANIMATION_DURATION);
      }, displayDuration);

      notiSchedulerRef.current = window.setTimeout(showNext, getNextDelay());
    };

    // Init shuffle
    notiShuffledRef.current = [...Array(NOTI_ITEMS.length).keys()].sort(
      () => Math.random() - 0.5,
    );

    notiSchedulerRef.current = window.setTimeout(showNext, NOTI_INITIAL_DELAY);

    return () => {
      if (notiSchedulerRef.current !== null) {
        window.clearTimeout(notiSchedulerRef.current);
      }
    };
  }, []);

  const runBannerTransition = React.useCallback(
    (previousIndex: number, nextIndex: number) => {
      if (previousIndex === nextIndex) return;
      if (rightBannerAnimationTimeoutRef.current !== null) {
        window.clearTimeout(rightBannerAnimationTimeoutRef.current);
      }
      setRightBannerState({ previous: previousIndex, isAnimating: true });
      rightBannerAnimationTimeoutRef.current = window.setTimeout(() => {
        setRightBannerState({ previous: null, isAnimating: false });
        rightBannerAnimationTimeoutRef.current = null;
      }, SIDE_BANNER_ANIMATION_DURATION);
    },
    [],
  );

  React.useEffect(() => {
    if (RIGHT_DESKTOP_BANNERS.length < 2) return;
    const timer = window.setInterval(() => {
      setRightBannerIndex((prev) => {
        const next = (prev + 1) % RIGHT_DESKTOP_BANNERS.length;
        runBannerTransition(prev, next);
        return next;
      });
    }, SIDE_BANNER_ROTATION_INTERVAL);
    return () => window.clearInterval(timer);
  }, [runBannerTransition]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const totalSlides = 3;

  const clampSlideIndex = React.useCallback(
    (index: number) => Math.max(0, Math.min(totalSlides - 1, index)),
    [totalSlides],
  );

  const scheduleTransitionUnlock = React.useCallback(() => {
    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current);
    }

    transitionTimeoutRef.current = window.setTimeout(() => {
      isTransitioningRef.current = false;
      transitionTimeoutRef.current = undefined;
    }, SLIDE_TRANSITION_DURATION);
  }, []);

  const goToSlide = React.useCallback(
    (target: number) => {
      const next = clampSlideIndex(target);
      if (next === activeSlide || isTransitioningRef.current) {
        return;
      }

      isTransitioningRef.current = true;
      scheduleTransitionUnlock();
      setActiveSlide(next);
    },
    [activeSlide, clampSlideIndex, scheduleTransitionUnlock],
  );

  const changeSlideBy = React.useCallback(
    (delta: number) => {
      if (delta === 0 || isTransitioningRef.current) {
        return;
      }

      const next = clampSlideIndex(activeSlide + delta);
      if (next === activeSlide) {
        return;
      }

      isTransitioningRef.current = true;
      scheduleTransitionUnlock();
      setActiveSlide(next);
    },
    [activeSlide, clampSlideIndex, scheduleTransitionUnlock],
  );

  const handleRegisterWithFocus = React.useCallback(
    (location: string) => {
      trackRegisterCta(location);
      goToSlide(0);

      // Focus vào username input sau khi slide transition hoàn tất
      setTimeout(() => {
        usernameInputRef.current?.focus();
      }, SLIDE_TRANSITION_DURATION + 100);
    },
    [goToSlide, trackRegisterCta],
  );

  const handleLogoClick = React.useCallback(() => {
    pushDataLayerEvent("lp_click_logo", { location: "header" });
    goToSlide(0);
  }, [goToSlide, pushDataLayerEvent]);

  const handleSectionLogoClick = React.useCallback(
    (location: "intro" | "certificate") => {
      pushDataLayerEvent("lp_click_logo", { location });
      goToSlide(0);
    },
    [goToSlide, pushDataLayerEvent],
  );

  const handleSlideDotClick = React.useCallback(
    (index: number) => {
      pushDataLayerEvent("lp_click_slide_nav", {
        target_slide: index,
        control: "nav_dot",
      });
      goToSlide(index);
    },
    [goToSlide, pushDataLayerEvent],
  );

  const handleScrollControlClick = React.useCallback(
    (direction: "up" | "down") => {
      const delta = direction === "up" ? -1 : 1;
      const target = clampSlideIndex(activeSlide + delta);
      pushDataLayerEvent("lp_click_slide_nav", {
        target_slide: target,
        control: "scroll_button",
        direction,
      });
      changeSlideBy(delta);
    },
    [activeSlide, changeSlideBy, clampSlideIndex, pushDataLayerEvent],
  );

  const findScrollableParent = React.useCallback(
    (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) {
        return null;
      }

      const sliderElement = sliderRef.current;
      let node: HTMLElement | null = target;

      while (node && node !== sliderElement) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;

        if (
          (overflowY === "auto" ||
            overflowY === "scroll" ||
            overflowY === "overlay") &&
          node.scrollHeight > node.clientHeight + 1
        ) {
          return node;
        }

        node = node.parentElement;
      }

      return null;
    },
    [sliderRef],
  );

  const shouldAllowScrollWithin = React.useCallback(
    (target: EventTarget | null, deltaY: number) => {
      if (deltaY === 0) {
        return false;
      }

      const scrollable = findScrollableParent(target);
      if (!scrollable) {
        return false;
      }

      if (deltaY > 0) {
        const canScrollDown =
          scrollable.scrollTop + scrollable.clientHeight <
          scrollable.scrollHeight - 1;
        return canScrollDown;
      }

      const canScrollUp = scrollable.scrollTop > 0;
      return canScrollUp;
    },
    [findScrollableParent],
  );

  React.useEffect(() => {
    const sliderNode = sliderRef.current;
    if (!sliderNode) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      if (shouldAllowScrollWithin(event.target, event.deltaY)) {
        return;
      }

      event.preventDefault();

      if (event.deltaY > 30) {
        changeSlideBy(1);
      } else if (event.deltaY < -30) {
        changeSlideBy(-1);
      }
    };

    sliderNode.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      sliderNode.removeEventListener("wheel", handleWheel);
    };
  }, [changeSlideBy, shouldAllowScrollWithin]);

  React.useEffect(() => {
    const sliderNode = sliderRef.current;
    if (!sliderNode) {
      return;
    }

    const handleTouchStart = (event: TouchEvent) => {
      touchStartYRef.current = event.touches[0]?.clientY ?? null;
      touchScrollTargetRef.current = findScrollableParent(event.target);
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (touchStartYRef.current === null) {
        return;
      }

      const currentY = event.touches[0]?.clientY ?? null;
      if (currentY === null) {
        return;
      }

      const deltaY = touchStartYRef.current - currentY;

      if (touchScrollTargetRef.current) {
        const scrollable = touchScrollTargetRef.current;
        if (deltaY > 0) {
          const canScrollDown =
            scrollable.scrollTop + scrollable.clientHeight <
            scrollable.scrollHeight - 1;
          if (canScrollDown) {
            touchStartYRef.current = currentY;
            return;
          }
        } else if (deltaY < 0) {
          const canScrollUp = scrollable.scrollTop > 0;
          if (canScrollUp) {
            touchStartYRef.current = currentY;
            return;
          }
        }
      }

      if (Math.abs(deltaY) > SLIDE_SWIPE_THRESHOLD) {
        event.preventDefault();
        changeSlideBy(deltaY > 0 ? 1 : -1);
        touchStartYRef.current = null;
        touchScrollTargetRef.current = null;
      }
    };

    const handleTouchEnd = () => {
      touchStartYRef.current = null;
      touchScrollTargetRef.current = null;
    };

    sliderNode.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    sliderNode.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    sliderNode.addEventListener("touchend", handleTouchEnd);
    sliderNode.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      sliderNode.removeEventListener("touchstart", handleTouchStart);
      sliderNode.removeEventListener("touchmove", handleTouchMove);
      sliderNode.removeEventListener("touchend", handleTouchEnd);
      sliderNode.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [changeSlideBy, findScrollableParent]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown" || event.key === "PageDown") {
        event.preventDefault();
        changeSlideBy(1);
      } else if (event.key === "ArrowUp" || event.key === "PageUp") {
        event.preventDefault();
        changeSlideBy(-1);
      } else if (event.key === "Home") {
        event.preventDefault();
        goToSlide(0);
      } else if (event.key === "End") {
        event.preventDefault();
        goToSlide(totalSlides - 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [changeSlideBy, goToSlide, totalSlides]);

  React.useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        window.clearTimeout(transitionTimeoutRef.current);
      }
      if (rightBannerAnimationTimeoutRef.current !== null) {
        window.clearTimeout(rightBannerAnimationTimeoutRef.current);
      }
    };
  }, []);

  const handleNavigate = React.useCallback(
    (url: string, target: "_self" | "_blank" = "_self") => {
      if (typeof window === "undefined") {
        return;
      }

      if (target === "_blank") {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      void flushEngagementThen(url);
    },
    [],
  );

  const openCreateAccount = React.useCallback(() => {
    setIsCreateAccountModalOpen(true);
  }, []);

  const handleRegisterCtaClick = React.useCallback(
    (location: string) => {
      trackRegisterCta(location);
      openCreateAccount();
    },
    [openCreateAccount, trackRegisterCta],
  );

  const handleCloseCreateAccount = React.useCallback(() => {
    setIsCreateAccountModalOpen(false);
  }, []);

  const handleOpenDownloadModal = React.useCallback(() => {
    setIsDownloadModalOpen(true);
  }, []);

  const handleCloseDownloadModal = React.useCallback(() => {
    setIsDownloadModalOpen(false);
  }, []);

  const handleOpenMobileDownloadModal = React.useCallback(
    (platform: "android" | "ios") => {
      setMobilePlatform(platform);
      setIsMobileDownloadModalOpen(true);
    },
    [],
  );

  const handleCloseMobileDownloadModal = React.useCallback(() => {
    setIsMobileDownloadModalOpen(false);
  }, []);

  const handleDownloadClick = React.useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    const userAgent = navigator.userAgent;
    const platform = detectPlatform(userAgent);

    pushDataLayerEvent("lp_click_cta_download", {
      platform: platform !== "unknown" ? platform : "android/ios",
      section: "top",
    });

    if (platform === "android") {
      // Mobile Android - mở modal mới với 3 nút
      handleOpenMobileDownloadModal("android");
    } else if (platform === "ios") {
      // Mobile iOS - mở modal mới với 2 nút
      handleOpenMobileDownloadModal("ios");
    } else {
      // Desktop - mở modal cũ với QR code
      handleOpenDownloadModal();
    }
  }, [
    handleOpenDownloadModal,
    handleOpenMobileDownloadModal,
    pushDataLayerEvent,
  ]);

  const handlePlayWebClick = React.useCallback(() => {
    pushDataLayerEvent("lp_click_cta_playweb", { section: "top" });
    handleNavigate(getPlayGameDomain());
  }, [handleNavigate, pushDataLayerEvent]);

  const handleSocialClick = React.useCallback(
    (
      platformId: string,
      url: string,
      target: "_self" | "_blank" = "_self",
      location: "hero" | "desktop" | "footer",
    ) => {
      const eventName =
        platformId === "telegram" ? "lp_click_social_Telegram" : "lp_click_social_FB";
      pushDataLayerEvent(eventName, {
        social_platform: platformId,
        location,
      });
      handleNavigate(url, target);
    },
    [handleNavigate, pushDataLayerEvent],
  );

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const trackedSlides = trackedSlidesRef.current;

    if (activeSlide === 1 && !trackedSlides.has(1)) {
      pushDataLayerEvent("lp_view_intro_section", { section_name: "intro" });
      trackedSlides.add(1);
    }

    if (activeSlide === 2 && !trackedSlides.has(2)) {
      pushDataLayerEvent("lp_view_footer_cert", { section_name: "certificate" });
      trackedSlides.add(2);
    }
  }, [activeSlide, pushDataLayerEvent]);

  return (
    <div className={styles.home}>
      <div className={styles.sliderViewport} ref={sliderRef}>
        <img
          className={styles.heroBackgroundDesktop}
          src={BackgroundDesktop}
          alt=""
          aria-hidden="true"
        />
        <img
          className={styles.heroBackgroundMobile}
          src={BackgroundMobile}
          alt=""
          aria-hidden="true"
        />
        <div
          className={styles.sliderTrack}
          style={{ transform: `translateY(-${activeSlide * 100}vh)` }}
        >
          <section
            id="section-hero"
            className={`${styles.slide} ${styles.hero}`}
          >
            <Container className={styles.heroContainer}>
              <div className={styles.heroContent}>
                <button
                  type="button"
                  id="logo"
                  className={`${styles.logoButton} ${styles.logoPrimary}`}
                  onClick={handleLogoClick}
                  aria-label="WinAZ"
                >
                  <img src={LogoWinAZ} alt="WinAZ" />
                </button>

                <img
                  src={TypoWinAZ}
                  alt="WinAZ - Đẳng cấp thượng lưu"
                  className={styles.logoTypo}
                />

                <div className={styles.heroFormCluster}>
                  <form
                    className={styles.loginCard}
                    style={{ backgroundImage: `url(${FormBackground})` }}
                    onSubmit={handleInlineSubmit}
                  >
                    <div className={styles.loginFields}>
                      <div className={styles.fieldGroup}>
                        <label
                          className={styles.inputWrapper}
                          style={{ backgroundImage: `url(${InputBackground})` }}
                        >
                          <img src={IconUser} alt="" aria-hidden="true" />
                          <input
                            ref={usernameInputRef}
                            type="text"
                            placeholder="Tên đăng nhập"
                            value={username}
                            onChange={handleUsernameChange}
                            onFocus={trackInlineFormStart}
                            disabled={isSubmitting}
                          />
                        </label>
                        {usernameError ? (
                          <div
                            className={styles.inlineError}
                            aria-live="assertive"
                          >
                            Vui lòng nhập tên đăng nhập
                          </div>
                        ) : null}
                      </div>
                      <div className={styles.fieldGroup}>
                        <label
                          className={styles.inputWrapper}
                          style={{ backgroundImage: `url(${InputBackground})` }}
                        >
                          <img src={IconPassword} alt="" aria-hidden="true" />
                          <input
                            type="password"
                            placeholder="Mật khẩu"
                            value={password}
                            onChange={handlePasswordChange}
                            onFocus={trackInlineFormStart}
                            disabled={isSubmitting}
                          />
                        </label>
                        {passwordError ? (
                          <div
                            className={styles.inlineError}
                            aria-live="assertive"
                          >
                            Vui lòng nhập mật khẩu
                          </div>
                        ) : null}
                      </div>
                      {turnstileRequired ? (
                        <div className={styles.turnstileGroup}>
                          <div className={styles.turnstileBlock}>
                            <div className={styles.turnstileInner}>
                              <TurnstileWidget
                                ref={turnstileWidgetRef}
                                className={styles.turnstileWidget}
                                onTokenChange={handleTurnstileTokenChange}
                              />
                            </div>
                          </div>
                        </div>
                      ) : null}
                      {apiError ? (
                        <div
                          className={styles.inlineError}
                          aria-live="assertive"
                        >
                          {apiError}
                        </div>
                      ) : null}
                    </div>

                    <motion.button
                      type="submit"
                      onClick={() => trackRegisterCta("hero_desktop")}
                      className={styles.registerButtonWrapper}
                      disabled={isSubmitting}
                      {...INTERACTIVE_IMAGE_PROPS}
                      data-cta
                    >
                      <img
                        src={LabelX3}
                        alt="Nạp Đầu x3"
                        className={styles.registerLabel}
                      />
                      <img
                        src={ButtonRegister}
                        alt="Đăng ký"
                        className={styles.registerButtonImage}
                      />
                    </motion.button>
                  </form>
                </div>

                <button
                  type="button"
                  className={styles.heroPlayButton}
                  id="btn-playweb"
                  onClick={handlePlayWebClick}
                >
                  <motion.img
                    {...INTERACTIVE_IMAGE_PROPS}
                    src={ButtonPlayWeb}
                    alt="Chơi bản web"
                  />
                </button>

                <div
                  className={styles.heroDownloadBlock}
                  style={{ backgroundImage: `url(${BgTaiGame})` }}
                >
                  <button
                    type="button"
                    className={styles.heroDownloadButton}
                    id="btn-download"
                    onClick={handleDownloadClick}
                  >
                    <motion.img
                      {...INTERACTIVE_IMAGE_PROPS}
                      src={ButtonDownload}
                      alt="Tải game"
                    />
                  </button>
                  <div className={styles.heroDownloadDivider} />
                  <div className={styles.heroSecurity}>
                    <div className={styles.heroSecurityIcons}>
                      <img src={IconBlockchain} alt="Blockchain" />
                      <img src={IconMd5} alt="MD5" />
                      <img src={IconGamingLabs} alt="Gaming Labs Certified" />
                    </div>
                    <p className={styles.heroSecurityText}>
                      <span className={styles.heroSecurityTextBold}>MINH BẠCH HOÀN TOÀN</span>
                      <br />
                      <span>CÔNG NGHỆ BLOCKCHAIN, MÃ HÓA MD5</span>
                    </p>
                  </div>
                </div>

                <div className={styles.heroProviders}>
                  <img src={ProviderLogo1} alt="Provider 1" />
                  <img src={ProviderLogo2} alt="Provider 2" />
                  <img src={ProviderLogo3} alt="Provider 3" />
                  <img src={ProviderLogo4} alt="Provider 4" />
                  <img src={ProviderLogo5} alt="Provider 5" />
                </div>

                <button
                  type="button"
                  className={styles.registerButtonMobile}
                  onClick={() => handleRegisterCtaClick("hero_mobile")}
                >
                  <motion.img
                    {...INTERACTIVE_IMAGE_PROPS}
                    src={ButtonRegister}
                    alt="Đăng ký"
                  />
                </button>

              </div>
            </Container>
          </section>

          <section
            id="intro-section"
            className={`${styles.slide} ${styles.introSection}`}
          >
            <Container className={styles.introContainer}>
              <button
                type="button"
                className={`${styles.logoButton} ${styles.logoPrimary}`}
                onClick={() => handleSectionLogoClick("intro")}
                aria-label="WinAZ"
              >
                <img src={LogoWinAZ} alt="WinAZ" />
              </button>
              <div className={styles.sectionLayout}>
                <div className={styles.sectionContent}>
                  <div className={styles.introInfoFrame}>
                    <div className={styles.introInfoBackground}>
                      <img src={FormBackground} alt="" />
                    </div>
                    <div className={styles.introInfoScroll}>
                      <h2>
                        WINAZ – CỔNG GAME BÀI 3D ĐỈNH CAO DÀNH CHO NGƯỜI VIỆT
                      </h2>
                      <p>
                        <strong>WinAZ</strong> là sân chơi{" "}
                        <strong>game bài 3D thuần giải trí</strong> với đồ hoạ
                        sống động, animation mượt và thao tác cực nhanh trên{" "}
                        <strong>app &amp; web</strong>. Vào game 1-2 chạm, sự
                        kiện dày (Đua Top, Hũ Lộc, nhiệm vụ ngày/tuần), CSKH
                        24/7.
                      </p>
                      <p>
                        Tất cả trò chơi đều được thiết kế đẹp mắt, dễ chơi, dễ
                        trúng – phù hợp mọi tay chơi Việt!
                      </p>

                      <img
                        className={styles.introImage}
                        src={IntroImage1}
                        alt="Sảnh mini game WinAZ"
                      />

                      <h3>MINI GAME – NHẸ NHÀNG, DỄ TRÚNG, VUI MỖI NGÀY!</h3>
                      <p>
                        <strong>Tài Xỉu 3D</strong>, <strong>Xóc Đĩa 3D</strong>
                        , <strong>Bầu Cua 3D</strong> – mô phỏng xúc xắc/chén
                        đĩa chân thực, nhịp chơi nhanh.
                      </p>
                      <p>
                        <strong>Mini Poker 3D</strong>,{" "}
                        <strong>Cao Thấp 3D</strong> – vòng ván gọn, hiệu ứng
                        lật/kéo bài mượt.
                      </p>
                      <img
                        className={styles.introImage}
                        src={IntroImage2}
                        alt="Danh mục mini game WinAZ"
                      />

                      <h3>
                        SLOT GAME – NỔ HŨ SIÊU KHỦNG, JACKPOT KHÔNG GIỚI HẠN!
                      </h3>
                      <p>
                        <strong>Hoa Quả Sơn</strong>,{" "}
                        <strong>Bạch Long Tam Thái Tử</strong>,{" "}
                        <strong>Chuyện Tấm Cám</strong>, <strong>Kẹo Nổ</strong>
                        , <strong>Tuyết Yêu Thương</strong> – chủ đề 3D bắt mắt,
                        nhiệm vụ theo màn.
                      </p>
                      <p>
                        <strong>Săn Cá - Mỹ Nhân Ngư</strong> – bắn cá 3D, map
                        rộng, boss theo đợt.
                      </p>
                      <img
                        className={styles.introImage}
                        src={IntroImage3}
                        alt="Danh mục slot game WinAZ"
                      />

                      <h3>CARDS GAME – GAME BÀI 3D ĐỈNH CAO, CHƠI LÀ MÊ!</h3>
                      <p>
                        <strong>Xì Dách</strong>,{" "}
                        <strong>Tiến Lên Miền Nam</strong>,{" "}
                        <strong>Mậu Binh</strong> – luật chơi quen thuộc, đồ hoạ
                        3D mượt mà.
                      </p>
                      <p>
                        <strong>Baccarat Live</strong>, <strong>Tài Xỉu</strong>
                        , <strong>Xóc Đĩa 3D</strong> – có livestream, dealer
                        xinh đẹp, cảm giác như đang ở sòng bài thật.
                      </p>
                      <p>
                        <strong>Aviator</strong>,{" "}
                        <strong>Lô Đề Siêu Tốc</strong> – nhanh gọn, dễ chơi, dễ
                        trúng - phù hợp với người bận rộn.
                      </p>
                      <img
                        className={styles.introImage}
                        src={IntroImage4}
                        alt="Danh mục game bài WinAZ"
                      />
                      <h2>
                        HỆ THỐNG BẢO MẬT &amp; HỖ TRỢ KHÁCH HÀNG CHUYÊN NGHIỆP
                      </h2>
                      <p>
                        <strong>WinAZ</strong> hiểu rằng, yếu tố then chốt để
                        giữ chân người chơi chính là sự an tâm. Trang web này
                        được vận hành dưới giấy phép quốc tế và sử dụng công
                        nghệ mã hoá tiên tiến nhất hiện nay để bảo vệ dữ liệu và
                        các giao dịch tài chính của người dùng. Mọi thông tin
                        đều được đảm bảo tuyệt mật và không bao giờ được chia sẻ
                        với bên thứ ba.
                      </p>

                      <h3>
                        UY TÍN &amp; BẢO MẬT – CHƠI AN TÂM, THẮNG LÀ CÓ THƯỞNG!
                      </h3>
                      <p>
                        <strong>Chứng nhận quốc tế:</strong> MGA, PAGCOR, PCI
                        DSS, iTech Labs – đảm bảo công bằng, minh bạch.
                      </p>
                      <p>
                        <strong>Bảo mật 2 lớp:</strong> Mã hoá SSL + xác thực
                        2FA – thông tin cá nhân và tài khoản được bảo vệ tuyệt
                        đối.
                      </p>
                      <p>
                        <strong>Giao dịch nhanh chóng:</strong> Nạp rút trong
                        15-30 giây, hỗ trợ Momo, ViettelPay, ngân hàng, USDT.
                      </p>
                      <img
                        className={styles.introImage}
                        src={IntroImage5}
                        alt="Chứng nhận bảo mật WinAZ"
                      />

                      <h3>ĐỘI NGŨ HỖ TRỢ KHÁCH HÀNG CHUYÊN NGHIỆP</h3>
                      <p>
                        Đội ngũ hỗ trợ khách hàng của Winaz hoạt động 24/7 thông
                        qua đa kênh:
                      </p>
                      <p>
                        <strong>Live Chat:</strong> Giải đáp ngay lập tức mọi
                        thắc mắc.
                      </p>
                      <p>
                        <strong>Email &amp; Hotline:</strong> Hỗ trợ xử lý kỹ
                        thuật hoặc hướng dẫn chi tiết.
                      </p>
                      <p>
                        <strong>Zalo:</strong> Kết nối gần gũi, thân thiện với
                        người dùng Việt.
                      </p>
                      <p>
                        Họ là những chuyên gia được đào tạo bài bản, luôn sẵn
                        sàng hỗ trợ tận tâm để đảm bảo bạn có trải nghiệm trọn
                        vẹn nhất.
                      </p>
                      <p>
                        <strong>Lưu ý:</strong>
                      </p>
                      <p>WinAZ chỉ dành cho người từ 18 tuổi trở lên.</p>
                      <p>
                        Hãy chơi có trách nhiệm – Giải trí là chính, đừng để đỏ
                        đen chi phối cuộc sống.
                      </p>
                    </div>
                  </div>

                  <motion.button
                    type="button"
                    className={styles.playWebButton}
                    onClick={() =>
                      handleRegisterWithFocus("intro_back_to_hero")
                    }
                    {...INTERACTIVE_IMAGE_PROPS}
                  >
                    <img
                      src={LabelX3}
                      alt="Nạp Đầu x3"
                      className={styles.registerLabel}
                    />
                    <img
                      src={ButtonRegister}
                      alt="Đăng ký"
                      className={styles.registerButtonImage}
                    />
                  </motion.button>

                  <motion.button
                    type="button"
                    className={styles.sectionRegisterButton}
                    onClick={() => handleRegisterWithFocus("intro_portrait")}
                    {...INTERACTIVE_IMAGE_PROPS}
                  >
                    <img
                      src={LabelX3}
                      alt="Nạp Đầu x3"
                      className={styles.registerLabel}
                    />
                    <img
                      src={ButtonRegister}
                      alt="Đăng ký"
                      className={styles.registerButtonImage}
                    />
                  </motion.button>

                  <button
                    type="button"
                    className={styles.registerButtonMobile}
                    onClick={() => handleRegisterCtaClick("intro_mobile")}
                  >
                    <motion.img
                      {...INTERACTIVE_IMAGE_PROPS}
                      src={ButtonRegister}
                      alt="Đăng ký"
                    />
                  </button>
                </div>
              </div>
            </Container>
          </section>

          <section
            id="certificate-section"
            className={`${styles.slide} ${styles.certificateSection}`}
          >
            <Container className={styles.certificateContainer}>
              <button
                type="button"
                className={`${styles.logoButton} ${styles.logoPrimary}`}
                onClick={() => handleSectionLogoClick("certificate")}
                aria-label="WinAZ"
              >
                <img src={LogoWinAZ} alt="WinAZ" />
              </button>
              <div className={styles.sectionLayout}>
                <div className={styles.sectionContent}>
                  <div className={styles.certificateInfoFrame}>
                    <div className={styles.certificateInfoBackground}>
                      <img src={FormBackground} alt="" />
                    </div>
                    <div className={styles.certificateInfoScroll}>
                      <h3>
                        HỆ THỐNG BẢO MẬT &amp; HỖ TRỢ KHÁCH HÀNG CHUYÊN NGHIỆP
                      </h3>
                      <p>
                        <strong>WinAZ</strong> hiểu rằng, yếu tố then chốt để
                        giữ chân người chơi chính là sự an tâm. Trang web này
                        được vận hành dưới giấy phép quốc tế và sử dụng công
                        nghệ mã hoá tiên tiến nhất hiện nay để bảo vệ dữ liệu và
                        các giao dịch tài chính của người dùng. Mọi thông tin
                        đều được đảm bảo tuyệt mật và không bao giờ được chia sẻ
                        với bên thứ ba.
                      </p>

                      <h3>
                        UY TÍN &amp; BẢO MẬT – CHƠI AN TÂM, THẮNG LÀ CÓ THƯỞNG!
                      </h3>
                      <p>
                        <strong>Chứng nhận quốc tế:</strong> MGA, PAGCOR, PCI
                        DSS, iTech Labs – đảm bảo công bằng, minh bạch.
                      </p>
                      <p>
                        <strong>Bảo mật 2 lớp:</strong> Mã hoá SSL + xác thực
                        2FA – thông tin cá nhân và tài khoản được bảo vệ tuyệt
                        đối.
                      </p>
                      <p>
                        <strong>Giao dịch nhanh chóng:</strong> Nạp rút trong
                        15-30 giây, hỗ trợ Momo, ViettelPay, ngân hàng, USDT.
                      </p>

                      <div className={styles.certificateGrid}>
                        {CERTIFICATE_IMAGES.map((imageSrc, index) => (
                          <img
                            key={imageSrc}
                            src={imageSrc}
                            alt={`Chứng nhận ${index + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <motion.button
                    type="button"
                    className={styles.playWebButton}
                    onClick={() =>
                      handleRegisterWithFocus("certificate_back_to_hero")
                    }
                    {...INTERACTIVE_IMAGE_PROPS}
                  >
                    <img
                      src={LabelX3}
                      alt="Nạp Đầu x3"
                      className={styles.registerLabel}
                    />
                    <img
                      src={ButtonRegister}
                      alt="Đăng ký"
                      className={styles.registerButtonImage}
                    />
                  </motion.button>

                  <motion.button
                    type="button"
                    className={styles.sectionRegisterButton}
                    onClick={() =>
                      handleRegisterWithFocus("certificate_portrait")
                    }
                    {...INTERACTIVE_IMAGE_PROPS}
                  >
                    <img
                      src={LabelX3}
                      alt="Nạp Đầu x3"
                      className={styles.registerLabel}
                    />
                    <img
                      src={ButtonRegister}
                      alt="Đăng ký"
                      className={styles.registerButtonImage}
                    />
                  </motion.button>

                  <button
                    type="button"
                    className={styles.registerButtonMobile}
                    onClick={() => handleRegisterCtaClick("certificate_mobile")}
                  >
                    <motion.img
                      {...INTERACTIVE_IMAGE_PROPS}
                      src={ButtonRegister}
                      alt="Đăng ký"
                    />
                  </button>
                </div>
              </div>
            </Container>
          </section>
        </div>
      </div>

      <div className={styles.desktopSideBannersWrapper}>
        <div
          className={`${styles.desktopSideBanner} ${styles.desktopSideBannerLeft}`}
        >
          <div className={styles.videoBannerWrapper}>
            <video
              ref={videoRef}
              id="videoPlayer"
              muted={isMuted}
              playsInline
              autoPlay
              loop
              src={
                resourceDomain
                  ? `https://${resourceDomain}/upload/streams/idol_live_1.mp4`
                  : undefined
              }
              className={styles.sideBannerVideo}
            />
            <button
              type="button"
              className={styles.videoMuteButton}
              onClick={() => setIsMuted((prev) => !prev)}
              aria-label={isMuted ? "Bật âm thanh" : "Tắt âm thanh"}
            >
              <img src={isMuted ? IconUnsound : IconSound} alt="" />
            </button>
          </div>
        </div>
        <div
          className={`${styles.desktopSideBanner} ${styles.desktopSideBannerRight}`}
        >
          <div className={styles.sideBannerViewport}>
            {rightBannerState.previous !== null && RIGHT_DESKTOP_BANNERS[rightBannerState.previous] ? (
              <img
                key={`right-prev-${rightBannerState.previous}`}
                src={RIGHT_DESKTOP_BANNERS[rightBannerState.previous].src}
                alt={RIGHT_DESKTOP_BANNERS[rightBannerState.previous].alt}
                aria-hidden="true"
                className={`${styles.sideBannerImage} ${
                  styles.sideBannerImagePrevious
                } ${
                  rightBannerState.isAnimating
                    ? styles.sideBannerImagePreviousAnimating
                    : ""
                }`}
              />
            ) : null}
            <img
              key={`right-current-${rightBannerIndex}`}
              src={RIGHT_DESKTOP_BANNERS[rightBannerIndex].src}
              alt={RIGHT_DESKTOP_BANNERS[rightBannerIndex].alt}
              className={`${styles.sideBannerImage} ${
                styles.sideBannerImageCurrent
              } ${
                rightBannerState.isAnimating
                  ? styles.sideBannerImageCurrentAnimating
                  : ""
              }`}
            />
          </div>
        </div>

        <div className={styles.notiSection}>
          {activeNotis.map((noti) => (
            <div
              key={noti.uid}
              className={`${styles.notiCard} ${noti.exiting ? styles.notiCardExiting : ""}`}
              style={{ backgroundImage: `url(${BgNoti})` }}
            >
              <div className={styles.notiCardText}>
                <p>
                  Chúc mừng <span className={styles.notiUsername}>{noti.item.username}</span> đã thắng
                </p>
                <p>
                  <span className={styles.notiPrizeInline}>{noti.item.prize}</span>{" "}
                  trong game <span className={styles.notiGame}>{noti.item.game}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.desktopSocials} aria-label="Liên kết mạng xã hội">
        <div className={styles.desktopSocialsInner}>
          <img
            src={LogoGalaxy}
            alt="Galaxy Macau Entertainment"
            className={styles.galaxyLogo}
          />
          <p className={styles.galaxyText}>
            Một Sản Phẩm Thuộc Tập Đoàn Galaxy Macau Entertainment
          </p>
          <div className={styles.socialButtons}>
            {SOCIAL_ITEMS.map((item) => (
              <button
                key={item.id}
                id={`desktop-social-${item.id}`}
                type="button"
                className={`social-icon-${item.id}`}
                onClick={() =>
                  handleSocialClick(
                    item.id,
                    item.url,
                    item.target ?? "_self",
                    "desktop",
                  )
                }
              >
                <img src={item.icon} alt={item.alt} />
              </button>
            ))}
          </div>
        </div>
      </div>

      <nav className={styles.slideDots} aria-label="Điều hướng slide">
        {[0, 1, 2].map((index) => (
          <button
            key={index}
            type="button"
            className={
              index === activeSlide ? styles.slideDotActive : styles.slideDot
            }
            aria-label={`Chuyển tới slide ${index + 1}`}
            aria-current={index === activeSlide}
            onClick={() => handleSlideDotClick(index)}
          />
        ))}
      </nav>

      <div
        className={styles.scrollControls}
        aria-label="Điều hướng slide theo chiều dọc"
      >
        {activeSlide > 0 ? (
          <button
            type="button"
            className={`${styles.scrollButton} ${styles.scrollButtonUp}`}
            onClick={() => handleScrollControlClick("up")}
          >
            <img src={ButtonScrollUp} alt="Lên slide trước" />
          </button>
        ) : null}

        {activeSlide < totalSlides - 1 ? (
          <button
            type="button"
            className={`${styles.scrollButton} ${styles.scrollButtonDown}`}
            onClick={() => handleScrollControlClick("down")}
          >
            <img src={ButtonScrollDown} alt="Xuống slide tiếp theo" />
          </button>
        ) : null}
      </div>

      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={handleCloseDownloadModal}
      />

      <MobileDownloadModal
        isOpen={isMobileDownloadModalOpen}
        onClose={handleCloseMobileDownloadModal}
        platform={mobilePlatform}
      />

      <CreateAccountModal
        isOpen={isCreateAccountModalOpen}
        onClose={handleCloseCreateAccount}
      />
    </div>
  );
};

export default Home;
